import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'auto'

const THEME_STORAGE_KEY = 'rivulet-theme'

function loadStoredTheme(): ThemeMode {
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'auto') {
    return stored
  }
  return 'auto'
}

export const useAppStore = defineStore('app', () => {
  // State
  const drawer = ref(false)
  const theme = ref<ThemeMode>(loadStoredTheme())
  const loading = ref(false)
  const title = ref(import.meta.env.VITE_APP_TITLE || 'Rivulet')
  const systemPrefersDark = ref(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  )

  // Getters
  const resolvedTheme = computed<'light' | 'dark'>(() => {
    if (theme.value === 'auto') {
      return systemPrefersDark.value ? 'dark' : 'light'
    }
    return theme.value
  })

  const isDark = computed(() => resolvedTheme.value === 'dark')

  const themeIcon = computed(() => {
    switch (theme.value) {
      case 'light': return 'mdi-weather-sunny'
      case 'dark': return 'mdi-weather-night'
      case 'auto': return 'mdi-theme-light-dark'
      default: return 'mdi-theme-light-dark'
    }
  })

  // Actions
  function toggleDrawer() {
    drawer.value = !drawer.value
  }

  function setDrawer(value: boolean) {
    drawer.value = value
  }

  function setTheme(value: ThemeMode) {
    theme.value = value
    localStorage.setItem(THEME_STORAGE_KEY, value)
  }

  function cycleTheme() {
    const order: ThemeMode[] = ['light', 'dark', 'auto']
    const currentIndex = order.indexOf(theme.value)
    const next = order[(currentIndex + 1) % order.length] as ThemeMode
    setTheme(next)
  }

  function setLoading(value: boolean) {
    loading.value = value
  }

  function initThemeListener() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', (e) => {
      systemPrefersDark.value = e.matches
    })
  }

  return {
    // State
    drawer,
    theme,
    loading,
    title,
    // Getters
    resolvedTheme,
    isDark,
    themeIcon,
    // Actions
    toggleDrawer,
    setDrawer,
    setTheme,
    cycleTheme,
    setLoading,
    initThemeListener,
  }
})
